import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader2, Users, PhoneCall, TrendingUp, ClipboardCheck, MessageSquare } from "lucide-react";
import { LEAD_STATUSES } from "@/lib/leadStatuses";

const Stat = ({ label, value, icon: Icon }) => (
    <div className="rounded-2xl bg-card border p-5" data-testid={`sales-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Icon className="w-5 h-5" /></div>
            <div>
                <div className="text-xs text-muted-foreground uppercase tracking-widest">{label}</div>
                <div className="font-display text-2xl font-bold">{value}</div>
            </div>
        </div>
    </div>
);

const SalesDashboard = () => {
    const [summary, setSummary] = useState(null);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const [s, l] = await Promise.all([
                api.get("/team/sales/summary"),
                api.get("/team/leads", { params: { scope: "mine" } }),
            ]);
            setSummary(s.data);
            setLeads(l.data.leads || []);
        } catch {}
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const setStatus = async (l, status) => {
        try { await api.patch(`/team/leads/${l.id}/status`, { status }); setLeads((xs) => xs.map((x) => x.id === l.id ? { ...x, status } : x)); toast.success("Updated"); }
        catch { toast.error("Failed"); }
    };

    if (loading) return <div className="p-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

    return (
        <div className="p-6 md:p-8" data-testid="team-sales">
            <div className="text-xs uppercase tracking-widest text-brand-gold font-semibold">Sales</div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Lead Pipeline</h1>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
                <Stat label="New leads" value={summary?.new_leads || 0} icon={Users} />
                <Stat label="Today" value={summary?.todays_leads || 0} icon={TrendingUp} />
                <Stat label="Contacted" value={summary?.contacted || 0} icon={PhoneCall} />
                <Stat label="Converted" value={summary?.converted || 0} icon={ClipboardCheck} />
                <Stat label="My tasks" value={summary?.my_tasks || 0} icon={MessageSquare} />
            </div>

            <div className="mt-8 rounded-2xl bg-card border overflow-hidden">
                <div className="p-4 border-b border-border flex items-center justify-between">
                    <h3 className="font-display font-semibold">Recent leads</h3>
                    <Link to="/team/tasks" className="text-xs text-primary">Go to task board →</Link>
                </div>
                <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-left">
                        <tr>
                            <th className="p-3">Name</th><th className="p-3">Contact</th><th className="p-3">City</th><th className="p-3">Occasion</th><th className="p-3">Status</th><th className="p-3">When</th>
                        </tr>
                    </thead>
                    <tbody>
                        {leads.slice(0, 30).map((l) => (
                            <tr key={l.id} className="border-t border-border" data-testid={`lead-row-${l.id}`}>
                                <td className="p-3 font-semibold">{l.name}</td>
                                <td className="p-3">
                                    <div className="text-xs">{l.email}</div>
                                    <div className="text-xs text-muted-foreground">{l.phone}</div>
                                </td>
                                <td className="p-3">{l.city || "—"}</td>
                                <td className="p-3">{l.occasion || "—"}</td>
                                <td className="p-3">
                                    <select value={l.status} onChange={(e) => setStatus(l, e.target.value)} className="rounded-lg border px-2 h-8 text-xs" data-testid={`sales-lead-status-${l.id}`}>
                                        {LEAD_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                    </select>
                                </td>
                                <td className="p-3 text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString()}</td>
                            </tr>
                        ))}
                        {leads.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-sm text-muted-foreground">No leads yet.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default SalesDashboard;
